import { useState } from "react";
import { Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { http } from "../../lib/api";
import { apiError } from "../../context/AuthContext";
import { UploadButton } from "./ImageUpload";
import { useAdminList, AdminHead, Field, TextInput, TextArea, Toggle, PrimaryBtn, csv, fromCsv, inputCls } from "./adminUi";

const BLANK = { name: "", region: "", country: "", kind: "city", subtitle: "", description: "", aliases: [], image: "", featured: false, order: 50 };

const DestForm = ({ initial, onDone }) => {
  const [d, setD] = useState({ ...BLANK, ...initial });
  const set = (k) => (e) => setD({ ...d, [k]: e.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...d, order: Number(d.order), image: d.image || null };
    try {
      if (initial.slug) await http.put(`/admin/destinations/${initial.slug}`, body);
      else await http.post("/admin/destinations", body);
      toast.success("Destination saved");
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="destination-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name"><TextInput required value={d.name} onChange={set("name")} data-testid="destination-form-name" /></Field>
        <Field label="Region / State"><TextInput value={d.region} onChange={set("region")} data-testid="destination-form-region" /></Field>
        <Field label="Country"><TextInput value={d.country} onChange={set("country")} /></Field>
        <Field label="Type"><select className={inputCls} value={d.kind} onChange={set("kind")}>{["city", "state", "island", "country", "region"].map((k) => <option key={k}>{k}</option>)}</select></Field>
      </div>
      <Field label="Subtitle (page tagline)"><TextInput value={d.subtitle} onChange={set("subtitle")} data-testid="destination-form-subtitle" /></Field>
      <Field label="Description (unique per destination for SEO)"><TextArea rows={3} value={d.description} onChange={set("description")} /></Field>
      <Field label="Search aliases" hint="Comma separated — e.g. nicknames, nearby towns"><TextInput value={csv(d.aliases)} onChange={(e) => setD({ ...d, aliases: fromCsv(e.target.value) })} /></Field>
      <Field label="Destination photo" hint="Wide landscape photos (1600px+) look best in the homepage slideshow.">
        <div className="space-y-2">
          {d.image && <img src={d.image} alt="" className="w-full h-36 object-cover rounded-xl" data-testid="destination-image-preview" />}
          <div className="flex gap-2 items-center">
            <UploadButton kind="destination" label="Upload photo" onUploaded={(url) => setD((cur) => ({ ...cur, image: url }))} testid="destination-image-upload" />
            <div className="flex-1"><TextInput placeholder="or paste an image URL" value={d.image || ""} onChange={set("image")} data-testid="destination-form-image" /></div>
          </div>
        </div>
      </Field>
      <div className="flex items-center gap-6">
        <Toggle label="Featured on homepage" checked={d.featured} onChange={(v) => setD({ ...d, featured: v })} testid="destination-form-featured" />
        <Field label="Sort order"><TextInput type="number" value={d.order} onChange={set("order")} /></Field>
      </div>
      <PrimaryBtn className="w-full" data-testid="destination-form-save">Save destination</PrimaryBtn>
    </form>
  );
};

export default function AdminDestinations() {
  const { data, reload } = useAdminList("/admin/destinations");
  const [edit, setEdit] = useState(null);
  return (
    <div data-testid="admin-destinations">
      <AdminHead title="Destinations" sub="Featured destinations appear in the homepage carousel. Assign products from the Products tab.">
        <PrimaryBtn onClick={() => setEdit({})} className="flex items-center gap-2" data-testid="add-destination-button"><Plus className="w-4 h-4" /> Add destination</PrimaryBtn>
      </AdminHead>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {data?.map((d) => (
          <button key={d.slug} onClick={() => setEdit(d)} className="text-left rounded-3xl bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`admin-destination-${d.slug}`}>
            <div className="h-28 ocean-gradient relative">{d.image && <img src={d.image} alt="" className="w-full h-full object-cover" />}{d.featured && <Star className="absolute top-3 right-3 w-5 h-5 fill-sun text-sun" />}</div>
            <div className="p-4"><p className="font-bold text-navy">{d.name}</p><p className="text-xs text-navy/60">{d.region} · {d.country} · /destinations/{d.slug}</p></div>
          </button>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="destination-dialog">
          <DialogTitle className="text-navy text-xl">{edit?.slug ? `Edit ${edit.name}` : "New destination"}</DialogTitle>
          {edit && <DestForm initial={edit} onDone={() => { setEdit(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
